/**
 * Thin vertical hit target on the right edge of a resizable table/list column.
 * Pairs with {@link useResizableColumnWidths}; keep `position` off sticky
 * `<th>` cells — render this inside an in-flow flex row at the column edge.
 */

import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { Box } from '@mantine/core';

/**
 * Shared visual/interaction style for column boundary grips.
 *
 * The divider line has to land ON the column boundary. Mantine pads `th` and
 * `td` by `--table-horizontal-spacing`, so a grip that simply sits at the end
 * of the header's content box draws its line roughly a padding-width plus its
 * own width short of the real edge. Body cells fill to the padding edge, so
 * their text ran past the header's line and the columns read as misaligned.
 *
 * Pulling the grip into the cell's trailing padding and drawing the border on
 * its right edge puts the line exactly on the boundary — and moves the drag
 * target onto the edge it actually resizes.
 */
export const COLUMN_RESIZE_GRIP_STYLE: CSSProperties = {
  flexShrink: 0,
  width: 14,
  minHeight: 28,
  cursor: 'col-resize',
  userSelect: 'none',
  touchAction: 'none',
  borderRight: '1px solid var(--mantine-color-gray-4)',
  marginLeft: 'var(--mantine-spacing-xs)',
  marginRight: 'calc(var(--table-horizontal-spacing, var(--mantine-spacing-xs)) * -1)',
};

export interface ColumnResizeGripProps {
  /** Shown in `aria-label` / `title` (e.g. "Time and Event Type"). */
  boundaryLabel: string;

  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
}

/**
 * Renders the draggable separator between two columns.
 */
export function ColumnResizeGrip({ boundaryLabel, onPointerDown }: ColumnResizeGripProps) {
  return (
    <Box
      onPointerDown={onPointerDown}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize between ${boundaryLabel}`}
      title={`Drag to resize (${boundaryLabel})`}
      style={COLUMN_RESIZE_GRIP_STYLE}
    />
  );
}
